import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import { ReactComponent as EditIcon } from 'src/Images/icons/common/EditIcon.svg';
import {
  getAttachedDeliveriesForVehicleTracking,
  updateVehicleTrackingDeliveryAttachments,
} from 'src/redux/Actions/VehicleTrackingOutward';
import VehicleTrackingDeliveryEditModal from 'src/components/VehicleTrackingDetails/OutwardContentForm/VehicleTrackingDeliveryEditModal';
import 'src/style/createVehicleTracking.css';

function VehicleTrackingDeliveryTab() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber } = useParams();
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer?.userData?.tenant?.theme;
  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const { vehicleTrackingDetails } = vehicleTrackingDataReducer || {};
  const vehicleNumber = vehicleTrackingDetails?.vehicleNumber;

  const vehicleTrackingOutwardReducer = useSelector(
    (state) => state?.VehicleTrackingOutwardReducer,
  );
  const { attachedDeliveriesForVehicleTracking } =
    vehicleTrackingOutwardReducer;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!organisationId || !vehicleTrackingNumber || !vehicleNumber) {
      return undefined;
    }

    setIsLoading(true);
    dispatch(
      getAttachedDeliveriesForVehicleTracking(
        organisationId,
        vehicleTrackingNumber,
      ),
    ).finally(() => setIsLoading(false));

    return undefined;
  }, [dispatch, organisationId, vehicleTrackingNumber, vehicleNumber]);

  const deliveryHeaderData =
    attachedDeliveriesForVehicleTracking?.delivery || [];

  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDeliveryObjList, setSelectedDeliveryObjList] = useState([]);

  const toggleEditDrawer = (open) => () => {
    setOpenEditDrawer(open);
    if (open) {
      setSelectedDeliveryObjList(deliveryHeaderData);
    }
  };

  const handleUpdateDeliverySubmit = () => {
    if (!organisationId || !vehicleTrackingNumber || !vehicleNumber) return;

    const payload = {
      tenant_organisation_id: organisationId,
      vehicle_tracking_number: vehicleTrackingNumber,
      vehicle_number: vehicleNumber,
      delivery_ids: selectedDeliveryObjList.map(
        (deliveryObj) => deliveryObj.delivery_id,
      ),
    };

    setIsSubmitting(true);
    dispatch(updateVehicleTrackingDeliveryAttachments(payload))
      // this is a backend check in case we need
      // it for future we can show case it on frontend
      .then((response) => {
        const responseBody = response?.data || response || {};
        const failToUpdateDeliveries =
          responseBody.blockedDeliveries ||
          responseBody.blocked_deliveries ||
          [];

        if (failToUpdateDeliveries.length > 0) {
          setSelectedDeliveryObjList((prev) => {
            const next = [...prev];
            failToUpdateDeliveries.forEach((deliveryId) => {
              const alreadySelected = next.some(
                (deliveryObj) => deliveryObj.delivery_id === deliveryId,
              );
              if (!alreadySelected) {
                const stillAttached = deliveryHeaderData.find(
                  (deliveryObj) => deliveryObj.delivery_id === deliveryId,
                );
                if (stillAttached) {
                  next.push(stillAttached);
                }
              }
            });
            return next;
          });

          return undefined;
        }

        setOpenEditDrawer(false);
        return dispatch(
          getAttachedDeliveriesForVehicleTracking(
            organisationId,
            vehicleTrackingNumber,
          ),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="div-flex-column container-card-shadow min-height-20vh">
      <div className="container-padding-5">
        <div className="container-padding-10">
          <div className="div-space-between div-align-center px-2 py-2">
            <span className="text-md text-medium-bold">Attach Deliveries</span>
            <button
              className="icon-btn-none"
              onClick={toggleEditDrawer(true)}
              aria-label="Edit Delivery"
              type="button"
            >
              <EditIcon className="icon-size-xl" />
            </button>
          </div>

          <div className="margin-top-5">
            <div className="purchase-order-line-item-card border-radius-4">
              <div
                className={`po-table-wrapper delivery-summary-table-scroll theme-scrollable-container-${themeColor}`}
              >
                <table>
                  <thead className="outward-delivery-table-head">
                    <tr>
                      <th>Delivery Number</th>
                      <th>Delivery Date</th>
                      <th>Pack Type</th>
                      <th>Pack Quantity</th>
                      <th>Grade</th>
                      <th>Gross Weight</th>
                      <th>Product SAP Code</th>
                      <th>Product Description</th>
                      <th>Plant</th>
                      <th>Net Weight</th>
                      <th>Batch</th>
                      <th>Sales Order Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(deliveryHeaderData || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          className="no-data-found-message border-bottom vertical-padding-10"
                        >
                          <span className="text-sm">
                            {isLoading
                              ? 'Loading...'
                              : 'No Delivery Data Found'}
                          </span>
                        </td>
                      </tr>
                    ) : (
                      deliveryHeaderData.map((deliveryObj) => {
                        const deliveryLineItems = deliveryObj.line_items || [];
                        const tableRows = deliveryLineItems.length
                          ? deliveryLineItems
                          : [{}];
                        const rowSpan = tableRows.length;

                        return tableRows.map((item, itemIndex) => (
                          <tr
                            key={
                              item?.delivery_line_item_id ||
                              `${deliveryObj.delivery_id}-${itemIndex}`
                            }
                          >
                            {itemIndex === 0 && (
                              <>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.sap_code ?? '-'}
                                </td>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.delivery_date ?? '-'}
                                </td>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.pack_type ?? '-'}
                                </td>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.pack_quantity ?? '-'}
                                </td>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.grade ?? '-'}
                                </td>
                                <td rowSpan={rowSpan}>
                                  {deliveryObj?.weight ?? '-'}
                                </td>
                              </>
                            )}
                            <td>{item?.product?.product_sap_code ?? '-'}</td>
                            <td>
                              {item?.product_description ||
                                item?.product?.product_description ||
                                '-'}
                            </td>
                            <td>{item?.plant?.name ?? '-'}</td>
                            <td>{item?.quantity ?? '-'}</td>
                            <td>{item?.batch ?? '-'}</td>
                            {itemIndex === 0 && (
                              <td rowSpan={rowSpan}>
                                {deliveryObj?.sales_order_number ?? '-'}
                              </td>
                            )}
                          </tr>
                        ));
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Drawer
        anchor="right"
        open={Boolean(openEditDrawer)}
        onClose={toggleEditDrawer(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: { width: '80%' },
        }}
        sx={{
          zIndex: 1,
        }}
      >
        <div className="div-flex-column rg-20 container-padding-20">
          <span className="text-xxl text-bold">Update Delivery</span>
          <VehicleTrackingDeliveryEditModal
            themeColor={themeColor}
            selectedDeliveryObjList={selectedDeliveryObjList}
            setSelectedDeliveryObjList={setSelectedDeliveryObjList}
            deliveryHeaderData={deliveryHeaderData}
            toggleEditDrawer={toggleEditDrawer}
            handleUpdateDeliverySubmit={handleUpdateDeliverySubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default VehicleTrackingDeliveryTab;
